/**
 * As constantes e o estado do formulário de produto.
 *
 * Vivem separados de `FormularioProduto.tsx` por um motivo prático: arquivo que
 * exporta componente E constante perde o hot reload do Vite (react-refresh só
 * consegue substituir um módulo cujos exports são todos componentes). O
 * formulário é a tela que mais se ajusta olhando — é justamente nele que
 * recarregar a página a cada linha custa caro.
 */
import type { Categoria, Cor } from '../../plataforma/produtos'

/**
 * Tamanhos oferecidos, por tipo de grade.
 *
 * O servidor recusa tamanho fora da grade DA LOJA (`grade_tamanho`), que é
 * escolhida no passo 3 do cadastro da conta. Esta lista é a do protótipo e
 * cobre o caso comum; quando a tela de "Grades e cores" existir, ela passa a
 * vir da API e esta constante sai. Até então, um tamanho fora da grade volta
 * como 422 nomeando o tamanho — e não como cadastro torto.
 */
export const TAMANHOS: Record<'roupa' | 'numeracao', string[]> = {
  roupa: ['PP', 'P', 'M', 'G', 'GG', 'XG', 'ÚNICO'],
  numeracao: ['34', '35', '36', '37', '38', '39', '40', '41', '42', '43', '44'],
}

/** Paleta de partida. A cor vive no SKU, não numa tabela (docs/de-para.md). */
export const PALETA: Cor[] = [
  { nome: 'Preto', hex: '#1F2937' },
  { nome: 'Branco', hex: '#F5F5F5' },
  { nome: 'Cinza', hex: '#9CA3AF' },
  { nome: 'Azul', hex: '#2563EB' },
  { nome: 'Azul-marinho', hex: '#1E3A8A' },
  { nome: 'Vermelho', hex: '#DC2626' },
  { nome: 'Verde militar', hex: '#4D5C3A' },
  { nome: 'Coral', hex: '#F97362' },
]

/** Meta de margem do vestuário — spec 01 e protótipo. Avisa, não bloqueia. */
export const META_DE_MARGEM = 4500

/**
 * O estado editável do formulário.
 *
 * Texto puro, inclusive preço e custo: a conversão para centavos é do servidor,
 * na borda (CONSTITUICAO.md §1). Guardar número aqui obrigaria a tela a
 * converter para exibir e a desconverter para enviar — duas travessias a mais
 * onde o centavo se perde.
 */
export type ValoresProduto = {
  nome: string
  codigo: string
  categoria: Categoria
  marca: string
  preco: string
  custo: string
  minimo: string
  tamanhos: string[]
  cores: string[]
}

export function valoresVazios(): ValoresProduto {
  return {
    nome: '',
    codigo: '',
    categoria: 'roupas',
    marca: '',
    preco: '',
    custo: '',
    minimo: '2',
    tamanhos: [],
    cores: [],
  }
}

export function tipoDeGradeDe(categoria: Categoria): 'roupa' | 'numeracao' {
  return categoria === 'tenis' ? 'numeracao' : 'roupa'
}

/**
 * Junta a paleta padrão com as cores que o produto JÁ usa, sem repetir.
 *
 * Sem isso, editar um produto de cor "Musgo" mostraria só as oito cores da
 * paleta — e salvar tiraria a Musgo da grade, desativando os SKUs dela, porque
 * a tela mandou de volta apenas o que sabia desenhar.
 */
export function juntarCores(padrao: Cor[], extras: Cor[]): Cor[] {
  const conhecidas = new Set(padrao.map((cor) => cor.nome.toLowerCase()))
  const juntas = [...padrao]
  for (const cor of extras) {
    if (conhecidas.has(cor.nome.toLowerCase())) continue
    conhecidas.add(cor.nome.toLowerCase())
    juntas.push(cor)
  }
  return juntas
}

/** O mesmo para tamanho fora da lista de partida. */
export function juntarTamanhos(padrao: string[], extras: string[]): string[] {
  const conhecidos = new Set(padrao)
  const juntos = [...padrao]
  for (const tamanho of extras) {
    if (conhecidos.has(tamanho)) continue
    conhecidos.add(tamanho)
    juntos.push(tamanho)
  }
  return juntos
}
